
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  HiOutlinePlay,
  HiOutlineVideoCamera,
  HiOutlineArrowUpRight,
  HiOutlineCalendarDays,
} from "react-icons/hi2";

import { getAllVideos } from "../../redux/actions/videos";
import VideoThumbnail from "./VideoThumbnail";

/* ============================================
   RELATED VIDEOS
============================================ */

function RelatedVideos({
  currentVideoId,
  currentVideoUrl,
  categoryId,
  category,
}) {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();

  const { isLoading, Allvideos } = useSelector(
    (state) => state.Videos || {}
  );

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  /* ======================================
     FETCH CATEGORY VIDEOS
  ====================================== */

  useEffect(() => {
    if (!categoryId) return;

    dispatch(getAllVideos(0, 12, categoryId));
  }, [dispatch, categoryId]);

  const videos = Array.isArray(Allvideos)
    ? Allvideos
    : [];

  const relatedVideos = videos
    .filter((video) => {
      const isCurrent =
        currentVideoId &&
        String(video._id) === String(currentVideoId);

      const sameUrl =
        currentVideoUrl &&
        video.url === currentVideoUrl;

      const sameCategory =
        categoryId &&
        String(video.categoryId) === String(categoryId);

      return !isCurrent && !sameUrl && sameCategory;
    })
    .slice(0, 6);

  const labels = {
    eyebrow: isEnglish
      ? "KEEP WATCHING"
      : "واصل المشاهدة",

    title: isEnglish
      ? "More Videos"
      : "فيديوهات أخرى",

    description: isEnglish
      ? "Other videos from this category."
      : "شروحات أخرى ضمن التصنيف نفسه.",

    watch: isEnglish
      ? "Watch Video"
      : "شاهد الفيديو",

    all: isEnglish
      ? "All Video Categories"
      : "جميع تصنيفات الفيديو",

    empty: isEnglish
      ? "No additional videos are available in this category."
      : "لا توجد فيديوهات إضافية في هذا التصنيف حاليًا.",
  };

  const scrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div
      className="
        overflow-hidden
        rounded-[22px]

        border border-[#D9E4EC]
        bg-white

        shadow-[0_12px_35px_rgba(15,39,55,0.045)]

        dark:border-white/10
        dark:bg-[#142B3D]
        dark:shadow-none
      "
    >
      {/* ======================================
          SIDEBAR HEADER
      ====================================== */}

      <div className="border-b border-[#E1E9EF] px-5 py-6 dark:border-white/10">

        <div className="mb-4 flex items-center justify-between">

          <span className="font-kufi text-xs font-semibold text-[#A47D41] dark:text-[#D7B77F]">
            {labels.eyebrow}
          </span>

          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#F0F5F8] text-[#C19B60] dark:bg-white/[0.07] dark:text-[#D7B77F]">
            <HiOutlineVideoCamera
              size={22}
              aria-hidden="true"
            />
          </div>
        </div>

        <h2 className="h5 text-[#19344D] dark:text-white">
          {labels.title}
        </h2>

        <p className="mt-2 font-kufi text-xs leading-6 text-[#71879A] dark:text-[#B5C8D6]">
          {labels.description}
        </p>
      </div>

      {/* ======================================
          RELATED VIDEOS LIST
      ====================================== */}

      <div className="max-h-[600px] space-y-2.5 overflow-y-auto p-3 sm:p-4">

        {isLoading ? (
          <div className="space-y-3 py-3">
            {[1, 2, 3, 4].map((item) => (
              <div
                key={item}
                className="flex animate-pulse gap-3 rounded-xl bg-[#F0F5F8] p-3 dark:bg-white/[0.06]"
              >
                <div className="h-[76px] w-[112px] shrink-0 rounded-lg bg-[#DCE5EB] dark:bg-white/10" />

                <div className="flex flex-1 flex-col gap-3 py-2">
                  <div className="h-3 w-full rounded bg-[#DCE5EB] dark:bg-white/10" />
                  <div className="h-3 w-2/3 rounded bg-[#DCE5EB] dark:bg-white/10" />
                </div>
              </div>
            ))}
          </div>
        ) : relatedVideos.length > 0 ? (
          relatedVideos.map((video) => {
            const title = isEnglish
              ? video.titleEn || video.titleAr
              : video.titleAr || video.titleEn;

            return (
              <Link
                key={video._id || video.url}
                to="/VedioDetails"
                state={{
                  VideoData: video,
                  category,
                }}
                onClick={scrollTop}
                aria-label={`${labels.watch}: ${title}`}
                className="
                  group flex min-w-0
                  items-center gap-3

                  rounded-xl
                  border border-transparent
                  bg-[#F4F7F9]
                  p-2.5

                  transition-all duration-300

                  hover:border-[#C19B60]/35
                  hover:bg-[#EDF3F7]

                  focus-visible:outline-none
                  focus-visible:ring-2
                  focus-visible:ring-[#C19B60]

                  dark:bg-white/[0.045]
                  dark:hover:border-[#D7B77F]/25
                  dark:hover:bg-white/[0.08]
                "
              >
                {/* Thumbnail */}
                <div className="relative w-[110px] shrink-0 overflow-hidden rounded-lg sm:w-[120px]">

                  <VideoThumbnail
                    image={video.Image}
                    alt=""
                    className="aspect-video w-full"
                  />

                  <div
                    aria-hidden="true"
                    className="absolute inset-0 bg-[#061522]/20"
                  />

                  <div
                    aria-hidden="true"
                    className="absolute left-1/2 top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/30 bg-[#102031]/60 text-white backdrop-blur-sm transition-transform duration-300 group-hover:scale-110"
                  >
                    <HiOutlinePlay size={18} />
                  </div>
                </div>

                {/* Video information */}
                <div className="flex min-w-0 flex-1 flex-col gap-2">

                  <h3
                    className="
                      line-clamp-2
                      font-kufi
                      text-xs font-semibold
                      leading-[1.85]

                      text-[#19344D]
                      dark:text-white

                      transition-colors duration-300
                      group-hover:text-[#A47D41]
                      dark:group-hover:text-[#D7B77F]
                    "
                  >
                    {title}
                  </h3>

                  {video.date && (
                    <div className="flex items-center gap-1.5 text-[#71879A] dark:text-[#AFC3D1]">

                      <HiOutlineCalendarDays
                        size={13}
                        aria-hidden="true"
                      />

                      <span className="font-kufi text-[11px]">
                        {video.date}
                      </span>
                    </div>
                  )}

                </div>
              </Link>
            );
          })
        ) : (
          <p className="px-3 py-9 text-center font-kufi text-sm leading-7 text-[#71879A] dark:text-[#B5C8D6]">
            {labels.empty}
          </p>
        )}
      </div>

      {/* ======================================
          SIDEBAR FOOTER
      ====================================== */}

      <div className="border-t border-[#E1E9EF] p-4 dark:border-white/10">

        <Link
          to="/VideoCategoryPage"
          onClick={scrollTop}
          className="
            group flex min-h-[46px]
            items-center justify-between
            gap-3

            rounded-xl
            bg-[#EDF3F7]

            px-4 py-3

            text-[#19344D]

            transition-colors duration-300
            hover:bg-[#E1EAF0]

            dark:bg-white/[0.06]
            dark:text-white
            dark:hover:bg-white/[0.10]
          "
        >
          <span className="font-kufi text-xs font-semibold">
            {labels.all}
          </span>

          <HiOutlineArrowUpRight
            size={18}
            aria-hidden="true"
            className="rtl:-scale-x-100 text-[#C19B60]"
          />
        </Link>
      </div>
    </div>
  );
}

export default RelatedVideos;
